import { capturePreviewSnapshot } from '@/utils/capturePreview';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Package, Shirt, Box, Link2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCart } from '@/context/CartContext';
import FabricStep from '@/components/FabricStep';
import OptionsStep, { PIPING_OPTIONS } from '@/components/OptionsStep';
import BayWindow3DPreview from '@/components/BayWindow3DPreview';
import FoamInlineSection from '@/components/FoamInlineSection';
import ThicknessButtons from '@/components/ThicknessButtons';
import { FOAM_BY_THICKNESS, FOAM_DENSITIES } from '@/components/cushionConstants';
import { CustomizerLayout, ProductTypeStep, buildProductImages, PreviewGallery, ReviewStep } from '@/components/customizer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CUSHION_ID = 'bay-window-bench-seat-cushion';

const GALLERY_IMAGES = [
  { src: 'https://images.unsplash.com/photo-1598295094949-829796d200d1?w=800&q=80', alt: 'Window seat with throw pillows' },
  { src: 'https://images.unsplash.com/photo-1547470895-300cde642d8e?w=800&q=80', alt: 'Gray fabric sofa with throw pillows' },
  { src: 'https://images.unsplash.com/photo-1693821747100-9f95f5692f81?w=800&q=80', alt: 'Couch in front of a window' },
  { src: 'https://images.unsplash.com/photo-1770537255527-56276fcf82a7?w=800&q=80', alt: 'Sunlight through window onto sofa' },
];

const PRODUCT_TYPE_OPTIONS = [
  { value: 'cover_and_foam', label: 'Cover & Foam Insert', description: 'Complete cushion with cover and foam', icon: Package },
  { value: 'cover_only', label: 'Cover Only', description: 'Just the fabric cover, no foam', icon: Shirt },
  { value: 'foam_only', label: 'Foam Insert Only', description: 'Just the foam insert, no cover', icon: Box },
];

// --- Trapezoid geometry helpers ---
const calculateSymmetricSide = (A, B, C) => {
  if (A <= B || A <= 0 || B <= 0 || C <= 0) return null;
  const offset = (A - B) / 2;
  return Math.sqrt(offset * offset + C * C);
};

const calculateRightSide = (A, B, C, D) => {
  if (A <= B || A <= 0 || B <= 0 || C <= 0 || D <= 0 || D < C) return null;
  const leftOffsetSquared = D * D - C * C;
  if (leftOffsetSquared < 0) return null;
  const leftOffset = Math.sqrt(leftOffsetSquared);
  const rightOffset = (A - B) - leftOffset;
  if (rightOffset < 0) return null;
  return Math.sqrt(rightOffset * rightOffset + C * C);
};

const validateTrapezoid = (A, B, C, D, isSymmetric) => {
  if (A <= 0 || B <= 0 || C <= 0) return { valid: false, error: 'Front, Back, and Depth must be greater than 0' };
  if (B >= A) return { valid: false, error: 'Back edge (B) must be shorter than Front edge (A)' };
  if (isSymmetric) {
    const symmetricD = calculateSymmetricSide(A, B, C);
    if (!symmetricD) return { valid: false, error: 'Invalid dimensions for symmetric shape' };
    return { valid: true, error: null, edgeD: symmetricD, edgeE: symmetricD };
  }
  if (D <= 0) return { valid: false, error: 'Left side (D) must be greater than 0' };
  if (D < C) return { valid: false, error: `Left side (D) must be at least ${Math.ceil(C)} cm (the depth)` };
  const E = calculateRightSide(A, B, C, D);
  if (!E || E <= 0) return { valid: false, error: "These dimensions don't form a valid trapezoid. Try adjusting D." };
  return { valid: true, error: null, edgeD: D, edgeE: E };
};

const BayWindowCustomizerPage = () => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(false);
  const [productType, setProductType] = useState('cover_and_foam');
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [activeField, setActiveField] = useState(null);
  const stepContentRef = useRef(null);
  const [cushion, setCushion] = useState(null);

  const [dimensionsCm, setDimensionsCm] = useState({ edgeA: 0, edgeB: 0, depth: 0, edgeD: 0, thickness: null });
  const [inputValues, setInputValues] = useState({ edgeA: '', edgeB: '', depth: '', edgeD: '' });
  const [unit, setUnit] = useState('cm');
  const [isSymmetric, setIsSymmetric] = useState(true);

  const [selectedFabric, setSelectedFabric] = useState(null);
  const [selectedFoam, setSelectedFoam] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [priceInfo, setPriceInfo] = useState(null);
  const [selectedFabricRange, setSelectedFabricRange] = useState(null);
  const [selectedFabricColor, setSelectedFabricColor] = useState(null);
  const [fabricZoom, setFabricZoom] = useState(3);
  const [fabricRanges, setFabricRanges] = useState([]);
  const [pipingOption, setPipingOption] = useState('none');
  const [pipingFabricRange, setPipingFabricRange] = useState(null);
  const [pipingFabricColor, setPipingFabricColor] = useState(null);
  const [galleryIndex, setGalleryIndex] = useState(0);

  const cmToUnit = useCallback((cm, u) => u === 'mm' ? cm * 10 : cm, []);
  const unitToCm = useCallback((v, u) => u === 'mm' ? v / 10 : v, []);

  const getDisplayValue = useCallback((field) => {
    if (inputValues[field] !== undefined && inputValues[field] !== '') return inputValues[field];
    const v = cmToUnit(dimensionsCm[field], unit);
    return v === 0 ? '' : v;
  }, [dimensionsCm, unit, cmToUnit, inputValues]);

  const handleDimensionInput = (field, rawValue) => {
    const filtered = rawValue.replace(/[^0-9.]/g, '');
    const parts = filtered.split('.');
    const clean = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : filtered;
    setInputValues(prev => ({ ...prev, [field]: clean }));
    if (clean === '') setDimensionsCm(prev => ({ ...prev, [field]: 0 }));
    else { const n = parseFloat(clean); if (!isNaN(n)) setDimensionsCm(prev => ({ ...prev, [field]: unitToCm(n, unit) })); }
  };
  const handleDimensionBlur = (field) => { setInputValues(prev => ({ ...prev, [field]: '' })); setActiveField(null); };
  const handleDimensionFocus = (field) => { const v = cmToUnit(dimensionsCm[field], unit); setInputValues(prev => ({ ...prev, [field]: v === 0 ? '' : String(v) })); setActiveField(field); };

  const formatSide = useCallback((cmValue) => {
    if (cmValue <= 0) return '-';
    const display = cmToUnit(cmValue, unit);
    return unit === 'cm' ? display.toFixed(2) : Math.round(display);
  }, [unit, cmToUnit]);

  const suggestedD = useMemo(() => {
    const { edgeA, edgeB, depth } = dimensionsCm;
    if (edgeA > edgeB && edgeA > 0 && edgeB > 0 && depth > 0) return calculateSymmetricSide(edgeA, edgeB, depth);
    return null;
  }, [dimensionsCm]);

  const calculatedSides = useMemo(() => {
    const { edgeA, edgeB, depth, edgeD } = dimensionsCm;
    if (isSymmetric) { const side = calculateSymmetricSide(edgeA, edgeB, depth); return { D: side || 0, E: side || 0 }; }
    const E = calculateRightSide(edgeA, edgeB, depth, edgeD);
    return { D: edgeD, E: E || 0 };
  }, [dimensionsCm, isSymmetric]);

  const fullDimensionsCm = useMemo(() => ({
    ...dimensionsCm, edgeD: calculatedSides.D, edgeE: calculatedSides.E,
    front_width: dimensionsCm.edgeA, back_width: dimensionsCm.edgeB, length: dimensionsCm.depth, width: dimensionsCm.edgeA
  }), [dimensionsCm, calculatedSides]);

  const [validationResult, setValidationResult] = useState({ valid: false, error: null });
  useEffect(() => {
    const { edgeA, edgeB, depth, edgeD } = dimensionsCm;
    if (isSymmetric) {
      if (edgeA > 0 && edgeB > 0 && depth > 0) setValidationResult(validateTrapezoid(edgeA, edgeB, depth, 0, true));
      else setValidationResult({ valid: false, error: null });
    } else {
      if (edgeA > 0 && edgeB > 0 && depth > 0 && edgeD > 0) setValidationResult(validateTrapezoid(edgeA, edgeB, depth, edgeD, false));
      else setValidationResult({ valid: false, error: null });
    }
  }, [dimensionsCm, isSymmetric]);

  const steps = useMemo(() => {
    const base = [{ name: 'Product', description: 'Select type' }, { name: 'Dimensions', description: 'Enter measurements' }];
    if (productType === 'cover_and_foam') return [...base, { name: 'Fabric', description: 'Choose fabric' }, { name: 'Foam', description: 'Select foam' }, { name: 'Options', description: 'Add extras' }, { name: 'Review', description: 'Confirm order' }];
    if (productType === 'cover_only') return [...base, { name: 'Fabric', description: 'Choose fabric' }, { name: 'Options', description: 'Add extras' }, { name: 'Review', description: 'Confirm order' }];
    return [...base, { name: 'Foam', description: 'Select foam' }, { name: 'Review', description: 'Confirm order' }];
  }, [productType]);
  const currentStepName = steps[currentStep]?.name || 'Product';

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [c, fr] = await Promise.all([axios.get(`${API}/cushion-types/${CUSHION_ID}`), axios.get(`${API}/fabric-ranges`)]);
        setCushion(c.data); setFabricRanges(fr.data);
      } catch { toast.error('Failed to load product data'); navigate('/'); }
      finally { setLoading(false); }
    })();
  }, [navigate]);

  const calculatePrice = useCallback(async () => {
    if (!cushion || !validationResult.valid) return;
    const hasFabric = selectedFabricRange && selectedFabricColor;
    if (productType === 'cover_and_foam' && (!hasFabric || !selectedFoam)) return;
    if (productType === 'cover_only' && !hasFabric) return;
    if (productType === 'foam_only' && !selectedFoam) return;
    try {
      const r = await axios.post(`${API}/calculate-price`, { cushion_type_id: CUSHION_ID, dimensions: fullDimensionsCm, product_type: productType, fabric_range_id: productType !== 'foam_only' ? selectedFabricRange : null, fabric_color_id: productType !== 'foam_only' ? selectedFabricColor : null, foam_type_id: productType !== 'cover_only' ? selectedFoam : null, quantity });
      setPriceInfo(r.data);
    } catch (e) { console.error('Price error:', e); }
  }, [cushion, fullDimensionsCm, productType, selectedFabricRange, selectedFabricColor, selectedFoam, quantity, validationResult.valid]);

  useEffect(() => { const t = setTimeout(calculatePrice, 300); return () => clearTimeout(t); }, [calculatePrice]);

  const handleThicknessChange = (v) => { const t = parseFloat(v); setDimensionsCm(prev => ({ ...prev, thickness: t })); const avail = FOAM_BY_THICKNESS[t] || []; if (selectedFoam && !avail.includes(selectedFoam)) setSelectedFoam(null); };

  const handleAddToCart = async () => {
    if (!priceInfo) return;
    setAddingToCart(true);
    const preview_image = await capturePreviewSnapshot();
    try {
      const ok = await addToCart({ cushion_type_id: CUSHION_ID, dimensions: fullDimensionsCm, product_type: productType, fabric_range_id: productType !== 'foam_only' ? selectedFabricRange : null, fabric_color_id: productType !== 'foam_only' ? selectedFabricColor : null, foam_type_id: productType !== 'cover_only' ? selectedFoam : null, quantity, zip_placement: 'back', piping_option: pipingOption !== 'none' ? pipingOption : null, piping_fabric_range_id: pipingOption === 'different' ? pipingFabricRange : null, piping_fabric_color_id: pipingOption === 'different' ? pipingFabricColor : null, preview_image });
      if (ok) toast.success('Added to cart!', { description: `Bay Window Cushion x${quantity}`, action: { label: 'View Cart', onClick: () => navigate('/cart') } });
      else toast.error('Failed to add to cart');
    } catch { toast.error('Failed to add to cart'); }
    finally { setAddingToCart(false); }
  };

  const nextStep = () => { if (currentStep < steps.length - 1) { setCurrentStep(currentStep + 1); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50); } };
  const prevStep = () => { if (currentStep > 0) { setCurrentStep(currentStep - 1); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50); } };

  const canProceed = () => {
    switch (currentStepName) {
      case 'Dimensions': return validationResult.valid && !!dimensionsCm.thickness;
      case 'Foam': return !!selectedFoam;
      case 'Fabric': return selectedFabricRange !== null && selectedFabricColor !== null;
      default: return true;
    }
  };

  const selectedFoamObj = selectedFoam ? FOAM_DENSITIES[selectedFoam] : null;
  const [searchFabricData, setSearchFabricData] = useState(null);
  const selectedFabricRangeObj = useMemo(() => fabricRanges.find(r => r.id === selectedFabricRange), [fabricRanges, selectedFabricRange]);
  const selectedFabricColorObj = useMemo(() => selectedFabricRangeObj?.colors?.find(c => c.id === selectedFabricColor), [selectedFabricRangeObj, selectedFabricColor]);
  const displayFabricName = useMemo(() => { if (searchFabricData) return `${searchFabricData.rangeName} - ${searchFabricData.colorName}`; return (selectedFabricRangeObj && selectedFabricColorObj) ? `${selectedFabricRangeObj.name} - ${selectedFabricColorObj.name}` : '-'; }, [selectedFabricRangeObj, selectedFabricColorObj, searchFabricData]);
  const fabricColorForPreview = selectedFabricColorObj?.hex_color || null;
  const fabricImageUrl = useMemo(() => { if (searchFabricData?.imageUrl) return searchFabricData.imageUrl; const u = selectedFabricColorObj?.image_url; return (u?.includes('warwick.com.au') || u?.startsWith('/api/fabric-images')) ? u : null; }, [selectedFabricColorObj, searchFabricData]);
  const pipingCost = useMemo(() => PIPING_OPTIONS.find(o => o.value === pipingOption)?.price || 0, [pipingOption]);
  const pipingHexColor = useMemo(() => {
    if (pipingOption === 'different' && pipingFabricRange && pipingFabricColor) {
      const pRange = fabricRanges.find(r => r.id === pipingFabricRange);
      return pRange?.colors?.find(c => c.id === pipingFabricColor)?.hex_color || '#f5f0eb';
    }
    return null;
  }, [pipingOption, pipingFabricRange, pipingFabricColor, fabricRanges]);
  const pipingImageUrl = useMemo(() => {
    if (pipingOption === 'same') return fabricImageUrl;
    if (pipingOption === 'different' && pipingFabricRange && pipingFabricColor) {
      const pRange = fabricRanges.find(r => r.id === pipingFabricRange);
      const pColor = pRange?.colors?.find(c => c.id === pipingFabricColor);
      const u = pColor?.image_url;
      return (u?.includes('warwick.com.au') || u?.startsWith('/api/fabric-images')) ? u : null;
    }
    return null;
  }, [pipingOption, fabricImageUrl, pipingFabricRange, pipingFabricColor, fabricRanges]);

  const configRows = useMemo(() => {
    const fmt = (cm) => cm > 0 ? `${unit === 'mm' ? Math.round(cm * 10) : Math.round(cm)} ${unit}` : '-';
    const rows = [
      { label: 'Product Type', value: PRODUCT_TYPE_OPTIONS.find(p => p.value === productType)?.label || '-' },
      { label: 'Thickness', value: dimensionsCm.thickness ? fmt(dimensionsCm.thickness) : '-' },
      { label: 'A (Front)', value: fmt(dimensionsCm.edgeA) },
      { label: productType !== 'foam_only' ? 'B (Back/Zip)' : 'B (Back)', value: fmt(dimensionsCm.edgeB) },
      { label: 'C (Depth)', value: fmt(dimensionsCm.depth) },
      { label: 'D (Left Side)', value: calculatedSides.D > 0 ? `${formatSide(calculatedSides.D)} ${unit}${isSymmetric ? ' (auto)' : ''}` : '-' },
      { label: 'E (Right Side)', value: calculatedSides.E > 0 ? `${formatSide(calculatedSides.E)} ${unit}${isSymmetric ? ' (symmetric)' : ''}` : '-' },
    ];
    if (productType !== 'foam_only' && (fabricColorForPreview || searchFabricData)) rows.push({ label: 'Fabric', value: displayFabricName });
    if (productType !== 'cover_only' && selectedFoamObj) rows.push({ label: 'Foam', value: selectedFoamObj.name });
    if (pipingOption !== 'none') {
      const pLabel = PIPING_OPTIONS.find(o => o.value === pipingOption)?.label || '-';
      if (pipingOption === 'different') {
        const pRange = fabricRanges.find(r => r.id === pipingFabricRange);
        const pColor = pRange?.colors?.find(c => c.id === pipingFabricColor);
        rows.push({ label: 'Piping', value: pColor ? <span className="flex items-center justify-end gap-2"><span className="w-4 h-4 rounded-full border" style={{ backgroundColor: pColor.hex_color }}></span>{pRange.name} - {pColor.name}</span> : pLabel });
      } else {
        rows.push({ label: 'Piping', value: pLabel });
      }
    }
    return rows;
  }, [productType, dimensionsCm, unit, calculatedSides, isSymmetric, formatSide, fabricColorForPreview, displayFabricName, searchFabricData, selectedFoamObj, pipingOption, fabricRanges, pipingFabricRange, pipingFabricColor]);

  // --- Dimension input helper (render function, NOT a component) ---
  const renderDimInput = (field, label, shortLabel, helpText, helpMobile) => (
    <div className="space-y-1 sm:space-y-2">
      <div className="flex items-center gap-1 sm:gap-2 relative">
        <Label className="text-xs sm:text-base font-semibold flex items-center gap-1 sm:gap-2">
          <span className="w-5 h-5 sm:w-8 sm:h-8 rounded-full bg-bush/10 flex items-center justify-center text-bush text-[10px] sm:text-sm font-bold">{shortLabel}</span>
          <span>{label}</span>
        </Label>
        <button className="sm:hidden w-4 h-4 rounded-full bg-gray-200 text-gray-500 text-[10px] font-semibold flex items-center justify-center" onClick={() => setActiveTooltip(activeTooltip === field ? null : field)}>i</button>
        {activeTooltip === field && (
          <div className="sm:hidden absolute left-0 bottom-full mb-2 z-50 w-52 p-2 bg-gray-900 text-white text-[11px] rounded-lg shadow-lg">
            <div className="absolute left-4 top-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-gray-900"></div>
            {helpText}
          </div>
        )}
      </div>
      <div className="relative">
        <Input type="text" inputMode="decimal" value={getDisplayValue(field)} onChange={(e) => handleDimensionInput(field, e.target.value)} onBlur={() => handleDimensionBlur(field)} onFocus={() => handleDimensionFocus(field)} placeholder="0" className="h-10 sm:h-14 text-base sm:text-xl font-semibold text-center bg-sand border-2 border-transparent focus:border-bush rounded-lg sm:rounded-xl pr-8 sm:pr-12 placeholder:text-gray-300" data-testid={`${field}-input`} />
        <span className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium text-xs sm:text-base">{unit}</span>
      </div>
      <p className="sm:hidden text-[10px] text-gray-400">{helpMobile || helpText}</p>
      {helpText && <p className="hidden sm:block text-xs text-gray-500 mt-[5px]">{helpText}</p>}
    </div>
  );

  const previewPanel = (
    <PreviewGallery galleryImages={GALLERY_IMAGES} galleryIndex={galleryIndex} setGalleryIndex={setGalleryIndex} configRows={configRows} priceInfo={priceInfo} pipingCost={pipingCost} showZipLabel={productType !== 'foam_only'}>
      <BayWindow3DPreview
        edgeA={dimensionsCm.edgeA > 0 ? dimensionsCm.edgeA : 100}
        edgeB={dimensionsCm.edgeB > 0 ? dimensionsCm.edgeB : 60}
        depth={dimensionsCm.depth > 0 ? dimensionsCm.depth : 50}
        edgeD={calculatedSides.D} edgeE={calculatedSides.E}
        thickness={dimensionsCm.thickness || 7.5} rawThickness={dimensionsCm.thickness}
        fabricColor={productType === 'foam_only' ? '#d4a843' : fabricColorForPreview} fabricImageUrl={productType === 'foam_only' ? '/foam-texture.png' : fabricImageUrl} fabricZoom={fabricZoom}
        showZip={productType !== 'foam_only'}
        rawEdgeA={dimensionsCm.edgeA} rawEdgeB={dimensionsCm.edgeB} rawDepth={dimensionsCm.depth}
        unit={unit} activeField={activeField}
        pipingOption={pipingOption} pipingColor={pipingHexColor} pipingImageUrl={pipingImageUrl}
      />
    </PreviewGallery>
  );

  const contentPanel = (
    <div ref={stepContentRef}>
      {currentStepName === 'Product' && <ProductTypeStep options={PRODUCT_TYPE_OPTIONS} value={productType} onChange={setProductType} productName="Bay Window Cushion" productDescription="Custom-shaped cushions designed to fit the unique angles of your bay window seat. Perfect for creating a cosy reading nook or a comfortable window perch, made precisely to your trapezoidal measurements." productImages={buildProductImages(GALLERY_IMAGES)} />}
      {currentStepName === 'Dimensions' && (
        <div data-testid="step-dimensions">
          <div className="flex items-center justify-between mb-2 sm:mb-4">
            <h2 className="text-lg font-semibold text-[#1A1A1A]">Enter Dimensions</h2>
            <Tabs value={unit} onValueChange={setUnit} className="w-auto"><TabsList className="h-7 sm:h-9 bg-sand p-0.5"><TabsTrigger value="cm" className="text-xs sm:text-sm px-2 sm:px-3 h-6 sm:h-8 data-[state=active]:bg-bush data-[state=active]:text-white rounded" data-testid="unit-cm">cm</TabsTrigger><TabsTrigger value="mm" className="text-xs sm:text-sm px-2 sm:px-3 h-6 sm:h-8 data-[state=active]:bg-bush data-[state=active]:text-white rounded" data-testid="unit-mm">mm</TabsTrigger></TabsList></Tabs>
          </div>
          <p className="hidden sm:block text-gray-600 mb-6 text-sm">Measure your bay window cushion area.</p>
          <div className="grid grid-cols-2 sm:grid-cols-1 gap-3 sm:gap-6">
            {renderDimInput('edgeA', 'Front Edge', 'A', 'The longest edge running along the front of the bay window', 'Longest edge')}
            {renderDimInput('edgeB', 'Back Edge', 'B', productType !== 'foam_only' ? 'Back edge with zip - must be smaller than A' : 'Back edge - must be smaller than A', productType !== 'foam_only' ? 'Zip location' : 'Back edge')}
            {renderDimInput('depth', 'Depth', 'C', 'Front to back measurement', 'Front to back')}

            {/* Thickness */}
            <div className="col-span-2 sm:col-span-1 space-y-1 sm:space-y-2 pt-0">
              <div className="flex items-center gap-1 sm:gap-2">
                <Label className="text-xs sm:text-base font-semibold flex items-center gap-1 sm:gap-2">
                  <span className="w-5 h-5 sm:w-8 sm:h-8 rounded-full bg-bush/10 flex items-center justify-center text-bush text-[10px] sm:text-sm font-bold">T</span>
                  <span>Thickness</span>
                </Label>
              </div>
              <ThicknessButtons value={dimensionsCm.thickness} onChange={handleThicknessChange} unit={unit} />
              <p className="hidden sm:block text-xs text-gray-500 mt-[5px]">Select foam thickness</p>
              <div className="sm:hidden h-[5px]" />
            </div>

            {/* Angled Sides */}
            <div className="col-span-2 sm:col-span-1 space-y-3 sm:space-y-4 pt-[10px] sm:pt-[20px] border-t sm:mt-0">
              <div className="flex items-center justify-between relative">
                <div className="flex items-center gap-1 sm:gap-2">
                  <Label className="text-xs sm:text-base font-semibold flex items-center gap-1 sm:gap-2">
                    <span className="w-5 h-5 sm:w-8 sm:h-8 rounded-full bg-bush/10 flex items-center justify-center text-bush text-[10px] sm:text-sm font-bold">D</span>
                    <span>Angled Sides</span>
                  </Label>
                  <button className="sm:hidden w-4 h-4 rounded-full bg-gray-200 text-gray-500 text-[10px] font-semibold flex items-center justify-center" onClick={() => setActiveTooltip(activeTooltip === 'angledSides' ? null : 'angledSides')}>i</button>
                  {activeTooltip === 'angledSides' && (
                    <div className="sm:hidden absolute left-0 bottom-full mb-2 z-50 w-64 p-2 bg-gray-900 text-white text-[11px] rounded-lg shadow-lg">
                      <div className="absolute left-4 top-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-gray-900"></div>
                      The diagonal side edges that connect the front and back edges. Use Symmetric for equal sides, or Independent to set them separately.
                    </div>
                  )}
                </div>
                <button onClick={() => setIsSymmetric(!isSymmetric)} className={`flex items-center gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm transition-colors ${isSymmetric ? 'bg-bush/10 text-bush' : 'bg-gray-200 text-gray-600'}`}>
                  <Link2 className={`h-3 w-3 sm:h-4 sm:w-4 ${isSymmetric ? '' : 'opacity-50'}`} />
                  {isSymmetric ? 'Symmetric' : 'Independent'}
                </button>
              </div>
              <p className="hidden sm:block text-xs text-gray-500 -mt-1">The diagonal side edges connecting front and back. Use Symmetric for equal sides, or Independent to set each side separately.</p>
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <Label className="text-xs sm:text-sm text-gray-600 flex items-center gap-1 h-5 sm:h-6 mb-1 sm:mb-2">
                    <span className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full ${isSymmetric ? 'bg-bush/30' : 'bg-bush/10'} flex items-center justify-center text-bush text-[9px] sm:text-[10px] font-bold`}>D</span>
                    Left {isSymmetric && <span className="text-[9px] text-gray-400">(auto)</span>}
                  </Label>
                  {isSymmetric ? (
                    <div className="h-10 sm:h-14 text-base sm:text-xl font-semibold text-center bg-gray-100 border-2 border-gray-200 rounded-lg sm:rounded-xl flex items-center justify-center text-bush">
                      {calculatedSides.D > 0 ? formatSide(calculatedSides.D) : '-'}
                      <span className="text-gray-400 font-medium text-xs sm:text-base ml-1">{unit}</span>
                    </div>
                  ) : (
                    <div className="relative">
                      <Input type="text" inputMode="decimal" value={getDisplayValue('edgeD')} onChange={(e) => handleDimensionInput('edgeD', e.target.value)} onBlur={() => handleDimensionBlur('edgeD')} onFocus={() => handleDimensionFocus('edgeD')} placeholder={suggestedD ? String(formatSide(suggestedD)) : "0"} className="h-10 sm:h-14 text-base sm:text-xl font-semibold text-center bg-sand border-2 border-transparent focus:border-bush rounded-lg sm:rounded-xl pr-8 sm:pr-12 placeholder:text-gray-300" data-testid="edge-d-input" />
                      <span className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium text-xs sm:text-base">{unit}</span>
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-xs sm:text-sm text-gray-600 flex items-center gap-1 h-5 sm:h-6 mb-1 sm:mb-2">
                    <span className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-bush/30 flex items-center justify-center text-bush text-[9px] sm:text-[10px] font-bold">E</span>
                    Right <span className="text-[9px] text-gray-400">(auto)</span>
                  </Label>
                  <div className="h-10 sm:h-14 text-base sm:text-xl font-semibold text-center bg-gray-100 border-2 border-gray-200 rounded-lg sm:rounded-xl flex items-center justify-center text-bush">
                    {calculatedSides.E > 0 ? formatSide(calculatedSides.E) : '-'}
                    <span className="text-gray-400 font-medium text-xs sm:text-base ml-1">{unit}</span>
                  </div>
                </div>
              </div>
            </div>

            {validationResult.error && (
              <div className="col-span-2 sm:col-span-1 bg-red-50 border border-red-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-red-700">{validationResult.error}</p>
              </div>
            )}
          </div>
        </div>
      )}
      {currentStepName === 'Foam' && <div data-testid="step-foam"><FoamInlineSection thicknessCm={dimensionsCm.thickness} selectedFoam={selectedFoam} onFoamSelect={setSelectedFoam} productType={productType} /></div>}
      {currentStepName === 'Fabric' && <FabricStep selectedRange={selectedFabricRange} selectedColor={selectedFabricColor} onRangeSelect={setSelectedFabricRange} onColorSelect={setSelectedFabricColor} cushionCategory={cushion?.category || 'indoor'} fabricZoom={fabricZoom} onFabricZoomChange={setFabricZoom} onSearchFabricSelect={setSearchFabricData} />}
      {currentStepName === 'Options' && <OptionsStep pipingOption={pipingOption} onPipingChange={setPipingOption} pipingFabricRange={pipingFabricRange} pipingFabricColor={pipingFabricColor} onPipingRangeSelect={setPipingFabricRange} onPipingColorSelect={setPipingFabricColor} cushionCategory={cushion?.category || 'indoor'} mainFabricRange={selectedFabricRange} mainFabricColor={selectedFabricColor} />}
      {currentStepName === 'Review' && <ReviewStep cushionName={cushion?.name || 'Bay Window Cushion'} productType={productType} productTypeLabel={PRODUCT_TYPE_OPTIONS.find(p => p.value === productType)?.label} dimensionsSummary={`${Math.round(dimensionsCm.edgeA)} x ${Math.round(dimensionsCm.depth)} x ${dimensionsCm.thickness} cm`} fabricColorForPreview={fabricColorForPreview} displayFabricName={displayFabricName} selectedFoamName={selectedFoamObj?.name} pipingOption={pipingOption} pipingCost={pipingCost} pipingFabricName={pipingOption === "different" ? (() => { const r = fabricRanges.find(r => r.id === pipingFabricRange); const c = r?.colors?.find(c => c.id === pipingFabricColor); return r && c ? `${r.name} - ${c.name}` : null; })() : null} quantity={quantity} setQuantity={setQuantity} priceInfo={priceInfo} extraRows={[{ label: 'A - Front Edge', value: `${Math.round(dimensionsCm.edgeA)} cm` }, { label: 'B - Back Edge', value: `${Math.round(dimensionsCm.edgeB)} cm` }, { label: 'C - Depth', value: `${Math.round(dimensionsCm.depth)} cm` }, { label: 'D - Left Side', value: `${formatSide(calculatedSides.D)} cm` }, { label: 'E - Right Side', value: `${formatSide(calculatedSides.E)} cm${isSymmetric ? ' (symmetric)' : ''}` }]} />}
    </div>
  );

  return <CustomizerLayout cushion={cushion} loading={loading} steps={steps} currentStepIndex={currentStep} currentStepName={currentStepName} onStepClick={setCurrentStep} isFirstStep={currentStep === 0} isReviewStep={currentStepName === 'Review'} canProceed={canProceed()} onBack={prevStep} onNext={nextStep} onAddToCart={handleAddToCart} addingToCart={addingToCart} priceInfo={priceInfo} pipingCost={pipingCost} pipingFabricName={pipingOption === "different" ? (() => { const r = fabricRanges.find(r => r.id === pipingFabricRange); const c = r?.colors?.find(c => c.id === pipingFabricColor); return r && c ? `${r.name} - ${c.name}` : null; })() : null} previewPanel={previewPanel} contentPanel={contentPanel} pageTestId="bay-window-customizer-page" />;
};

export default BayWindowCustomizerPage;
